import React, { Component } from 'react'
import { connect as connectRedux } from 'react-redux'
import { compose } from 'recompose'
import { bindActionCreators } from 'redux'
import { Map, List } from 'immutable'
import { format as formatUrl, parse as parseUrl } from 'url'
// eslint-disable-next-line import/extensions, import/no-unresolved
import { parse } from 'csv-parse/browser/esm'
import _defaultTo from 'lodash/defaultTo'
import _pick from 'lodash/pick'
import _omit from 'lodash/omit'
import _get from 'lodash/get'
import _debounce from 'lodash/debounce'
import _forEach from 'lodash/forEach'
import _startsWith from 'lodash/startsWith'
import Loader from 'gui/components/common/Loader'
import {
  Container,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import MuiForm from 'gui/components/common/MuiForm'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import MuiSelectWrapper from 'gui/components/common/MuiSelect'
import MuiButton from 'gui/components/common/MuiButton'
import MenuItem from '@mui/material/MenuItem'
import Pagination from 'gui/components/pagination'
import { createLocationHref } from 'services/location'
import {
  getRedirects as getRedirectsAction,
  createRedirect as createRedirectAction,
  removeRedirect as removeRedirectAction,
  updateRedirect as updateRedirectAction,
  clearError as clearRedirectAction,
  setBulkRedirects as setBulkRedirectsAction,
  processBulkUpload as processBulkUploadAction,
} from 'services/url-redirects/redux/actions'
import connectHistory from 'gui/components/router/connect'
import './index.scss'

const bulkStoreKey = 'bulkList'

function renderLoader () {
  return (
    <div className="loader-container">
      <Loader />
      <div className="loader__update-mask" />
    </div>
  )
}

function setRedirectData (editableEntity, href) {
  const url = parseUrl(href)
  const query = _defaultTo(url.query, '')
  const fragment = _defaultTo(url.hash, '').replace(/^#/, '')
  return editableEntity.merge({
    redirect: formatUrl(_omit(url, ['search', 'query', 'hash'])),
    query,
    fragment,
  })
}

function createEmpty () {
  return Map({
    source: '',
    redirect: '',
    query: '',
    fragment: '',
    type: null,
    id: null,
  })
}

function renderBulkRow ({ redirect, ii }) {
  const redirectSource = redirect.get('source')
  const redirectRedirect = redirect.get('redirect')
  const redirectType = redirect.get('type')
  const hasError = redirect.get('errorFlag')
  const redirectKey = `redirect-row${ii}`
  const rowClassName = hasError ? 'bulk-table__row bulk-table__row--error' : 'bulk-table__row'
  return (
    <tr key={redirectKey} className={rowClassName}>
      <td>{redirectSource}</td>
      <td>{redirectRedirect}</td>
      <td>{redirectType}</td>
    </tr>
  )
}

class RedirectsListDataTable extends Component {
  constructor (props) {
    super(props)
    const { location } = props
    const query = location.getIn(['data', 'query']) || new Map()
    this.state = {
      editableEntity: null,
      bulkLoadFiles: false,
      bulkFileView: false,
      bulkFileError: false,
      filterSource: query.get('source') || '',
      filterRedirect: query.get('redirect') || '',
      filterStatusCode: query.get('statusCode') || '',
    }
  }

  componentDidMount () {
    this.setFilterOptionValueDebounced = _debounce(this.setFilterOptionValue, 700)
  }

  componentDidUpdate (prevProps) {
    const { clearError, redirects, location } = this.props
    const error = redirects.get('error')
    if (error) {
      alert(error)
      clearError()
    }
    // Sync local filter state if location/query changes (e.g. navigation)
    if (location !== prevProps.location) {
      const query = location.getIn(['data', 'query']) || new Map()
      this.setState({
        filterSource: query.get('source') || '',
        filterRedirect: query.get('redirect') || '',
        filterStatusCode: query.get('statusCode') || '',
      })
    }
  }

  /**
   * Handle an onchange event from pagination
   */
  onChangePage = (page) => {
    const { redirects, location, storeKey } = this.props
    const offset = redirects.getIn([storeKey, 'limit']) * (page - 1)
    global.location.href = createLocationHref(location.setIn(['data', 'query', 'offset'], offset))
  }

  onEditChange = (e, field) => {
    e.preventDefault()
    const { state } = this
    let { editableEntity } = state
    const { value } = e.target
    editableEntity =
      field !== 'redirectHref'
        ? editableEntity.set(field, value)
        : setRedirectData(editableEntity, value)
    this.setState({
      formIsDirty: true,
      editableEntity,
    })
  }

  setFilterOptionValue = ({ value, filter }) => {
    const { history, location } = this.props
    let href
    const newLocation = location.setIn(['data', 'query', 'offset'], 0)
    if (value === '') {
      href = createLocationHref(newLocation.deleteIn(['data', 'query', filter]))
    } else {
      href = createLocationHref(newLocation.setIn(['data', 'query', filter], value))
    }
    history.replace(href)
  }

  onChangeFilterOption = (e, filter) => {
    const { value } = e.target
    // Update local state immediately
    if (filter === 'source') {
      this.setState({ filterSource: value })
    } else if (filter === 'redirect') {
      this.setState({ filterRedirect: value })
    } else if (filter === 'statusCode') {
      this.setState({ filterStatusCode: value })
    }
    // Debounce the Redux/URL update
    this.setFilterOptionValueDebounced({ value, filter })
  }

  render () {
    const { onChangeFilterOption } = this
    const { filterSource, filterRedirect, filterStatusCode } = this.state
    return (
      <Container
        className="redirects-list-data-table redirects-list-data-table--list"
        maxWidth={false}
      >
        <MuiForm className="redirects-filters">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" component="h3" className="redirects-filters__title">
                Filter Redirects
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className="redirects-filters__filter-item">
              <MuiTextFieldWrapper
                className="redirects-filters__input"
                label="Source"
                type="text"
                value={filterSource}
                onChange={(e) => onChangeFilterOption(e, 'source')}
                fullWidth
                variant="outlined"
                noMargin
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className="redirects-filters__filter-item">
              <MuiTextFieldWrapper
                className="redirects-filters__input"
                label="Redirect URL"
                type="text"
                value={filterRedirect}
                onChange={(e) => onChangeFilterOption(e, 'redirect')}
                fullWidth
                variant="outlined"
                noMargin
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} className="redirects-filters__filter-item">
              <MuiSelectWrapper
                label="Status Code"
                name="statusCode"
                value={filterStatusCode}
                onChange={(e) => onChangeFilterOption(e, 'statusCode')}
                className="redirects-filters__input"
                fullWidth
                noMargin
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="301">301</MenuItem>
                <MenuItem value="302">302</MenuItem>
              </MuiSelectWrapper>
            </Grid>
          </Grid>
        </MuiForm>
        <div className="redirect__controls">
          <MuiButton className="redirect__add" onClick={this.editRedirect(null)}>
            Add New Redirect
          </MuiButton>
          <MuiButton className="redirect__add" onClick={this.bulkUpload(null)}>
            Bulk Upload
          </MuiButton>
        </div>
        {this.renderBody()}
        {this.renderEditView()}
        {this.renderBulkLoad()}
        {this.renderBulkView()}
      </Container>
    )
  }

  renderBody = () => {
    const { props } = this
    const { storeKey, redirects } = props
    const processing = redirects.getIn([storeKey, 'processing'])
    const data = redirects.getIn([storeKey, 'data'], List())

    if (data.size === 0) {
      return (
        <div className="redirects-empty-state">
          <Typography variant="h5" component="h2" className="redirects-empty-state__title">
            No redirects found
          </Typography>
          <Typography variant="body1" className="redirects-empty-state__subtitle">
            Try adjusting your filters or add a new redirect
          </Typography>
        </div>
      )
    }
    if (processing) {
      return renderLoader()
    }
    return this.renderList()
  }

  renderList = () => {
    const { props, onChangePage } = this
    const { storeKey, redirects } = props
    const offset = redirects.getIn([storeKey, 'offset'])
    const limit = redirects.getIn([storeKey, 'limit'])
    const data = redirects.getIn([storeKey, 'data'], List())
    return (
      <div className="redirects-data-table">
        <Paper elevation={2} className="redirects-data-table__container">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow className="redirects-data-table__header-row">
                  <TableCell className="redirects-data-table__header-cell">Source</TableCell>
                  <TableCell className="redirects-data-table__header-cell">Redirect URL</TableCell>
                  <TableCell className="redirects-data-table__header-cell">
                    Full Redirect URL
                  </TableCell>
                  <TableCell className="redirects-data-table__header-cell">Status Code</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((redirect, i) => {
                  const redirectSource = redirect.get('source')
                  const redirectRedirect = redirect.get('redirect')
                  const redirectType = redirect.get('type')
                  const redirectId = redirect.get('id', null)
                  const redirectFull = redirect.get('fullUrl')
                  const redirectKey = redirectId
                    ? `redirect-row-${redirectId}`
                    : `redirect-row-${i}`

                  return (
                    <TableRow
                      key={redirectKey}
                      className="redirects-data-table__row"
                      onClick={this.editRedirect(redirect)}
                    >
                      <TableCell>
                        <Typography component="span" className="redirects-data-table__cell-link">
                          {redirectSource}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" className="redirects-data-table__cell-text">
                          {redirectRedirect}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" className="redirects-data-table__cell-text">
                          {redirectFull}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" className="redirects-data-table__cell-text">
                          {redirectType}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <div className="redirects-pagination">
          <Pagination
            onChangePage={onChangePage}
            page={offset / limit + 1}
            itemsPerPage={redirects.getIn([storeKey, 'limit'])}
            totalItemCount={redirects.getIn([storeKey, 'total'])}
          />
        </div>
      </div>
    )
  }

  //* ****************************EDIT REDIRECT*********************************
  editRedirect (redirect) {
    return () => {
      const n = redirect === null ? createEmpty() : redirect
      if (n) {
        this.setState(() => ({
          editableEntity: n,
          formIsDirty: false,
        }))
      }
    }
  }

  cancelRedirectOnClick = (e) => {
    e.preventDefault()
    this.setState(() => ({
      editableEntity: null,
    }))
  }

  saveRedirectOnClick = (e) => {
    e.preventDefault()

    const { state, props } = this
    const { createRedirect, updateRedirect, storeKey } = props
    const { editableEntity, formIsDirty } = state
    // console.log(`Save redirect, dirty=${formIsDirty}, entity=${JSON.stringify(editableEntity)}`)
    if (formIsDirty) {
      // if something has changed, otherwise ignore
      // validate no spaces in source field
      const source = editableEntity.get('source')
      if (source.indexOf(' ') !== -1) {
        alert('Source field must not contain any spaces!\nPlease fix...')
        return
      }
      const redirect = editableEntity.get('redirect')
      if (redirect.indexOf(' ') !== -1) {
        alert('Redirect field must not contain any spaces!\nPlease fix...')
        return
      }
      // validate no leading slash
      if (_startsWith(source, '/')) {
        alert('Source field must not begin with a slash character!\nPlease fix...')
        return
      }
      if (_startsWith(redirect, '/')) {
        alert('Redirect field must not begin with a slash character!\nPlease fix...')
        return
      }
      // validate source and redirect not the same (no self-referential redirects)
      if (source === redirect) {
        alert('Redirect field must be different than source!\nPlease fix...')
        return
      }
      // now good to save it off
      const id = editableEntity.get('id')
      if (id > 0) {
        updateRedirect({ storeKey, data: editableEntity })
      } else {
        createRedirect({ storeKey, data: editableEntity })
      }
    }
    this.setState(() => ({
      editableEntity: null,
      formIsDirty: false,
    }))
  }

  removeRedirectOnClick = (e) => {
    e.preventDefault()
    const { state, props } = this
    const { removeRedirect, storeKey } = props
    const { editableEntity } = state
    const id = editableEntity.get('id')
    // console.log(props)
    if (id) {
      removeRedirect({ storeKey, id })
    }

    this.setState(() => ({
      editableEntity: null,
    }))
  }

  renderEditView () {
    const { state, onEditChange } = this
    const { editableEntity } = state

    if (editableEntity) {
      const editSource = editableEntity.get('source')
      const parsedRedirect = parseUrl(editableEntity.get('redirect'))
      const editRedirectHref = formatUrl({
        ...parsedRedirect,
        search: editableEntity.get('query'),
        hash: editableEntity.get('fragment'),
      })
      const editType = editableEntity.get('type')
      const editId = editableEntity.get('id', null)
      const createdAt = new Date(editableEntity.get('createdAt', Date.now()))
      const editCreatedAt = createdAt.toString()
      const updatedAt = new Date(editableEntity.get('updatedAt', Date.now()))
      const editUpdatedAt = updatedAt.toString()
      return (
        <div className="redirects-editable">
          <div className="redirects-editable__body">
            <MuiForm className="redirects-editable__form">
              <Typography variant="h4" component="h1" gutterBottom>
                Edit Redirect
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MuiTextFieldWrapper
                    label="Source"
                    type="text"
                    required={true}
                    value={editSource || ''}
                    onChange={(e) => onEditChange(e, 'source')}
                    data-property="source"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MuiTextFieldWrapper
                    label="Redirect"
                    type="text"
                    required={true}
                    value={editRedirectHref || ''}
                    onChange={(e) => onEditChange(e, 'redirectHref')}
                    data-property="redirectHref"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <MuiSelectWrapper
                    label="Status Code"
                    name="statusCode"
                    value={editType || ''}
                    onChange={(e) => onEditChange(e, 'type')}
                    required={true}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="301">301</MenuItem>
                    <MenuItem value="302">302</MenuItem>
                  </MuiSelectWrapper>
                </Grid>
                <Grid item xs={5}>
                  <MuiTextFieldWrapper
                    label="Created at"
                    type="text"
                    disabled
                    value={editCreatedAt || ''}
                  />
                </Grid>
                <Grid item xs={5}>
                  <MuiTextFieldWrapper
                    label="Updated at"
                    type="text"
                    disabled
                    value={editUpdatedAt || ''}
                  />
                </Grid>
              </Grid>
              <div className="redirects-editable__actions">
                <MuiButton onClick={this.cancelRedirectOnClick}>Cancel</MuiButton>
                {editId ? <MuiButton onClick={this.removeRedirectOnClick}>Remove</MuiButton> : null}
                <MuiButton onClick={this.saveRedirectOnClick}>Save</MuiButton>
              </div>
            </MuiForm>
          </div>
        </div>
      )
    }
    return null
  }

  //* ****************************BULK UPLOAD **********************************
  /**
   * Handle request to start bulk-upload procedure
   */
  bulkUpload () {
    return () => {
      this.setState(() => ({
        bulkLoadFiles: true,
        bulkUploadFiles: List(),
      }))
    }
  }

  /**
   * Handle request to cancel all parts of bulk-upload
   */
  cancelBulkUploadOnClick = (event) => {
    event.preventDefault()
    this.setState(() => ({
      bulkLoadFiles: false,
      bulkDataView: false,
      bulkFileError: false,
    }))
  }

  /**
   * Handle request to process all entries of bulk-upload
   */
  performBulkUploadOnClick = (event) => {
    event.preventDefault()
    const { props } = this
    const { redirects, processBulkUpload, storeKey } = props
    // NOTE: bulk data NOT in main list storeKey!
    const data = redirects.getIn([bulkStoreKey, 'data'], List())
    processBulkUpload({ storeKey, data })

    this.setState(() => ({
      bulkLoadFiles: false,
      bulkDataView: false,
      bulkFileError: false,
    }))
  }

  uploadOnDrop = (event) => {
    event.preventDefault()
    const { props } = this
    const { setBulkRedirects } = props

    _forEach(event.dataTransfer.files, async (file) => {
      const { type } = file
      if (type !== 'text/csv') {
        alert('File type must be .csv for bulk upload')
        this.cancelBulkUploadOnClick(event)
        return
      }
      const fileReader = new FileReader()
      fileReader.onload = (fileEvent) => {
        const csvText = fileEvent.target.result
        let rowError = 0
        parse(csvText, (error, output) => {
          if (error) {
            alert(`Error while processing csv file: ${error}`)
            return
          }
          let data = List()
          _forEach(output, (row) => {
            // check for obvious problems
            let errorFlag = 0
            const source = _get(row, '0')
            if (source.indexOf(' ') !== -1 || source.length >= 2047) {
              errorFlag = 1
              rowError = true
            }
            const redirect = _get(row, '1')
            if (redirect.indexOf(' ') !== -1 || redirect.length >= 2047) {
              errorFlag = 2
              rowError = true
            }
            const rowType = _get(row, '2')
            if (!(rowType === '301' && rowType !== '302')) {
              errorFlag = 3
              rowError = true
            }
            const redirectItem = Map({
              source,
              redirect,
              type: rowType,
              errorFlag,
            })
            data = data.push(redirectItem)
          })
          setBulkRedirects({ storeKey: bulkStoreKey, data })
          this.setState(() => ({
            bulkLoadFiles: false,
            bulkDataView: true,
            bulkFileError: rowError,
          }))
        })
      }
      fileReader.readAsText(file)
    })
  }

  renderBulkLoad () {
    const { state } = this
    const { bulkLoadFiles } = state

    if (bulkLoadFiles) {
      // if here, we are in processing of finding a csv for bulkupload
      return (
        <div className="redirect-upload-bulk">
          <div className="redirect-upload-bulk__body">
            <Typography variant="h4" component="h1" gutterBottom>
              Redirects Bulk Upload
            </Typography>
            <div
              className={'redirect-upload-bulk__drop'}
              onDrop={this.uploadOnDrop}
              onDragOver={(event) => event.preventDefault()}
            >
              {
                <div className="redirect-upload-bulk__instructions">
                  Drag one or more files here...
                </div>
              }
            </div>
            <div className="redirect-upload-bulk__actions">
              <MuiButton onClick={this.cancelBulkUploadOnClick}>Cancel</MuiButton>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  renderBulkView () {
    const { state } = this
    const { bulkDataView, bulkFileError } = state
    if (bulkDataView) {
      // if here, show redirects bulk uploaded and ask for add
      const { props } = this
      const { redirects } = props
      const data = redirects.getIn([bulkStoreKey, 'data'], List())
      return (
        <div className="bulk-table">
          <div className="bulk-table__body">
            <Typography variant="h4" component="h1" gutterBottom>
              Uploaded Redirects
            </Typography>
            {bulkFileError && (
              <div className="bulk-table__error">
                <Typography variant="h6" component="h3" gutterBottom>
                  One or more redirects (in red) are not valid (spaces found, incorrect type, or URL
                  length greater than 2047).
                </Typography>
                <Typography variant="h6" component="h3" gutterBottom>
                  Please correct and resubmit
                </Typography>
              </div>
            )}
            <table className="bulk-table__table">
              <thead>
                <tr>
                  <th>Source:</th>
                  <th>Redirect:</th>
                  <th>Status Code:</th>
                </tr>
              </thead>
              <tbody>{data.map((redirect, ii) => renderBulkRow({ redirect, ii }))}</tbody>
            </table>
            <div className="bulk-table__actions">
              <MuiButton onClick={this.cancelBulkUploadOnClick}>Cancel</MuiButton>
              {!bulkFileError && (
                <MuiButton onClick={this.performBulkUploadOnClick}>Process Bulk Upload</MuiButton>
              )}
            </div>
          </div>
        </div>
      )
    }
    return null
  }
}

export default compose(
  connectRedux(
    (state) => _pick(state, ['redirects', 'location']),
    (dispatch) =>
      bindActionCreators(
        {
          getRedirects: getRedirectsAction,
          removeRedirect: removeRedirectAction,
          createRedirect: createRedirectAction,
          updateRedirect: updateRedirectAction,
          setBulkRedirects: setBulkRedirectsAction,
          processBulkUpload: processBulkUploadAction,
          clearError: clearRedirectAction,
        },
        dispatch,
      ),
  ),
  connectHistory(),
)(RedirectsListDataTable)
